import { useInfiniteQuery } from '@tanstack/react-query';
import { useFlag } from '@unleash/proxy-client-react';
import { useMemo } from 'react';

import { ConsumerGenerator } from '../components/dynamicPages/CreatePage/test-prep-tab/consumer-generator';
import backendService from '../services/backendService';
import { notUndefined } from '../utils/general';
import { SearchReducerstate } from './useGeneratorsQueryState';
import { useMetrics } from './useMetrics';

export const useQueryGenerators = ({
  searchState,
  preSetFilter,
}: {
  searchState: SearchReducerstate;
  preSetFilter?: string;
}) => {
  const metrics = useMetrics();
  const useTestingGeneratorsFlag = useFlag('useTestingGeneratorsFlag');
  const generatorStatus = useMemo(() => {
    return useTestingGeneratorsFlag ? ['staging', 'published'] : ['published'];
  }, [useTestingGeneratorsFlag]);
  const cacheKey = useMemo(() => {
    const key = [
      'generators-search',
      ...searchState.categories,
      preSetFilter,
      ...generatorStatus,
      ...searchState.grades,
      ...searchState.subjects,
      searchState.searchPhrase,
    ].filter(notUndefined);
    return key;
  }, [generatorStatus, preSetFilter, searchState]);

  const {
    data: generators,
    isFetching: isFetchingGenerators,
    isFetchingNextPage: isFetchingNextPageGenerators,
    hasNextPage: hasNextPageGenerators,
    fetchNextPage: fetchNextPageGenerators,
  } = useInfiniteQuery<{
    items: ConsumerGenerator[];
    cursor: number;
    total: number;
  }>(
    cacheKey,
    ({ pageParam = 0 }) => {
      const categories = preSetFilter
        ? [...searchState.categories, preSetFilter]
        : searchState.categories || [];

      metrics.logEvent('Generator.Query', {
        searchPhrase: searchState.searchPhrase,
        subjects: searchState.subjects,
        grades: searchState.grades,
        categories: categories.filter(notUndefined),
      });

      return backendService.instance.generators.search({
        limit: 15,
        cursor: parseInt(pageParam),
        subjects: searchState.subjects || [],
        grades: searchState.grades || [],
        categories: categories.filter(notUndefined),
        statuses: generatorStatus,
        searchPhrase: searchState.searchPhrase,
      });
    },
    {
      staleTime: 1000 * 60 * 5,
      getNextPageParam: lastPage => {
        return lastPage.cursor;
      },
    },
  );

  const generatorsItems = useMemo(
    () => generators?.pages?.flatMap(page => page.items) || [],
    [generators?.pages],
  );

  return {
    generators: generatorsItems,
    total: generators?.pages[0]?.total || 0,
    isFetchingGenerators,
    isFetchingNextPageGenerators,
    hasNextPageGenerators,
    fetchNextPageGenerators,
  };
};
